import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { authApi } from '@wordpuppi/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ThemeToggle } from '@/components/ThemeToggle';

export const Route = createFileRoute('/reset')({
  validateSearch: (search: Record<string, unknown>): { token?: string } => ({
    token: typeof search.token === 'string' ? search.token : undefined,
  }),
  component: ResetPasswordPage,
});

/** The reset email links to `/reset#<token>` (token in the URL fragment). */
function readHashToken(): string {
  if (typeof window === 'undefined') return '';
  const raw = window.location.hash.replace(/^#/, '');
  try {
    return decodeURIComponent(raw);
  } catch {
    // Malformed percent-encoding in an untrusted fragment — use it verbatim
    // rather than crashing the page render.
    return raw;
  }
}

function ResetPasswordPage() {
  const { token: queryToken } = Route.useSearch();
  // Fragment is the emailed format; query string is a fallback.
  const [token] = useState(() => readHashToken() || queryToken || '');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'done'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setStatus('submitting');
    try {
      await authApi.resetPassword({ token, password });
      setStatus('done');
    } catch (err) {
      setStatus('idle');
      setError(err instanceof Error ? err.message : 'Could not reset your password.');
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center p-4">
      <div className="absolute top-4 right-4">
        <ThemeToggle />
      </div>
      <Card className="w-full max-w-[400px]">
        <CardHeader>
          <CardTitle className="text-center text-2xl">Set a new password</CardTitle>
        </CardHeader>
        <CardContent>
          {status === 'done' ? (
            <div className="space-y-4 text-center">
              <div className="p-3 bg-muted rounded-lg text-sm text-muted-foreground">
                Your password has been reset. You can now sign in with your new password.
              </div>
              <Button asChild className="w-full">
                <Link to="/login">Sign in</Link>
              </Button>
            </div>
          ) : !token ? (
            <div className="space-y-4 text-center">
              <div className="p-3 bg-destructive/10 text-destructive rounded-lg text-sm">
                This reset link is invalid or incomplete. Please request a new one.
              </div>
              <Button asChild variant="outline" className="w-full">
                <Link to="/forgot">Request a new link</Link>
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 bg-destructive/10 text-destructive rounded-lg text-sm">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="password">New password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                />
                <p className="text-xs text-muted-foreground">At least 8 characters.</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm">Confirm password</Label>
                <Input
                  id="confirm"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                />
              </div>

              <Button type="submit" disabled={status === 'submitting'} className="w-full">
                {status === 'submitting' ? 'Saving...' : 'Reset password'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
